// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkoutMainElementStyles } from "@bg/App/SharedStyles/CheckoutShipping/CheckoutStyles";
import { CustomAppState, xCheckoutActions, xCheckoutState } from "@bg/App/Store/Reducers";
import BillingAddressInfoDisplay from "@bg/Overrides/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import PickUpLocationAddressInfoDisplay from "@bg/Overrides/Widgets/CheckoutReviewAndSubmit/PickUpLocationAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@bg/Overrides/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId, requestedDeliveryDate, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const session = getSession(state);

    let deliveryDateDisplay;
    let pickUpDateDisplay;

    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    const pickUpWarehouse = cart?.defaultWarehouse || session.pickUpWarehouse;

    if (cart) {
        if (cart.requestedDeliveryDate || state.pages.checkoutReviewAndSubmit.requestedDeliveryDate) {
            const tempDate = cart.requestedDeliveryDateDisplay
                ? cart.requestedDeliveryDateDisplay
                : state.pages.checkoutReviewAndSubmit.requestedDeliveryDate;
            deliveryDateDisplay = getLocalizedDateTime({
                dateTime: new Date(tempDate!),
                language: session.language,
            });
        }
        if (cart.requestedPickupDate || state.pages.checkoutReviewAndSubmit.requestedPickupDate) {
            const tempDate = cart.requestedPickupDateDisplay
                ? cart.requestedPickupDateDisplay
                : state.pages.checkoutReviewAndSubmit.requestedPickupDate;
            pickUpDateDisplay = getLocalizedDateTime({
                dateTime: new Date(tempDate!),
                language: session.language,
            });
        }
    }

    return {
        cartId,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        deliveryDateDisplay,
        pickUpDateDisplay,
        fulfillmentMethod,
        pickUpWarehouse,
        shippingPageNavLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        xNotes: state.xCheckout.notes,
        xCarrier: state.xCheckout.carrier,
        xShipVia: state.xCheckout.shipVia,
        xShipNameOfCarrier: state.xCheckout.nameOfCarrier,
        xShipAccountNumber: state.xCheckout.accountNumber,
        xShipShippingNotes: state.xCheckout.shippingNotes,
        xLastFour: state.xCheckout.lastFour,
        xExpDateMon: state.xCheckout.expDateMon,
        xExpDateYear: state.xCheckout.expDateYear,
        xCcType: (state.xCheckout.ccType && state.xCheckout.ccType.toLowerCase()) || "",
        xPo: state.xCheckout.po,
        xPaymentMethod: state.xCheckout.paymentMethod,
        xPaymentProfile: state.xCheckout.paymentProfile,
        requestedPickupDate,
        requestedDeliveryDate,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isPickUp: cart?.fulfillmentMethod === FulfillmentMethod.PickUp,
        isGuest: state.context.session?.isGuest,
    };
};

const mapDispatchToProps = {
    loadShipTo,
    loadBillTo,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps & HasHistory;

export interface CheckoutReviewAndSubmitShippingInfoStyles {
    stepWrapper?: InjectableCss;
    stepReviewTitle?: InjectableCss;
    stepReviewTitleText?: TypographyProps;
    stepReviewTitleOthers?: InjectableCss;
    stepReviewContent?: InjectableCss;
    container?: GridContainerProps;
    gridItemStyles?: GridItemProps;
    labelTypo?: TypographyProps;
    valueTypo?: TypographyProps;
    editIconStyles?: IconPresentationProps;
    clickableLinkStyles?: ClickablePresentationProps;
    headingText?: TypographyPresentationProps;
    pickupheading?: InjectableCss;
}

export const shippingInfoStyles: CheckoutReviewAndSubmitShippingInfoStyles = {
    container: {
        gap: 10,
        css: css``,
    },
    gridItemStyles: {
        width: [12, 6, 6, 6, 6],
    },
    editIconStyles: {
        size: fontConfig.fontSize.small,
        color: "secondary",
        css: css`
            fill: ${getColor("secondary")};
            margin-right: 3px;
        `,
    },
    stepReviewTitle: {
        css: css`
            width: 100%;
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            border-top: 2px solid ${getColor("common.ui3")};
            padding-top: 20px;

            @media print {
                break-inside: avoid;
            }
        `,
    },
    stepReviewTitleText: {
        variant: "h4",
    },
    stepReviewTitleOthers: {
        css: css`
            display: flex;
        `,
    },
    clickableLinkStyles: {
        css: css`
            color: ${getColor("secondary")};

            &,
            span {
                font-size: ${fontConfig.fontSize.xSmall}px;
                line-height: ${fontConfig.lineHeight.singleLine};
            }

            &:hover,
            &:focus {
                text-decoration: underline;
            }
        `,
    },
    stepReviewContent: {
        css: css`
            margin-top: 33px;
            margin-bottom: 40px;

            @media print {
                margin-bottom: 10px;
            }

            .guest {
                .cust-number__name {
                    display: none;
                }
            }
        `,
    },
    labelTypo: {
        css: css`
            display: inline;
            padding-right: 5px;
        `,
        weight: fontConfig.weight.semiBold,
        lineHeight: "20px",
    },
    valueTypo: {
        css: css`
            width: 100%;
            display: inline;
        `,
        lineHeight: "20px",
    },
    headingText: {
        variant: "h5",
    },
    pickupheading: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = shippingInfoStyles;

const CheckoutReviewAndSubmitShippingInfo: FC<Props> = ({
    shippingPageNavLink,
    shipToState,
    billToState,
    loadShipTo,
    cart,
    fulfillmentMethod,
    pickUpWarehouse,
    history,
    loadBillTo,
    cartId,
    xNotes,
    xCarrier,
    xShipVia,
    xLastFour,
    xExpDateMon,
    xExpDateYear,
    xShipNameOfCarrier,
    xShipAccountNumber,
    xShipShippingNotes,
    xCcType,
    xPo,
    xPaymentMethod,
    xPaymentProfile,
    requestedPickupDate,
    requestedDeliveryDate,
    cartPageUrl,
    id,
    pickUpDateDisplay,
    deliveryDateDisplay,
    isPickUp,
    isGuest,
}) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart || !shipToState.value || !billToState.value) {
        return null;
    }

    const goBackToShipping = () => {
        if (!shippingPageNavLink) {
            return;
        }

        const backUrl = cartId ? `${shippingPageNavLink.url}?cartId=${cartId}` : shippingPageNavLink.url;
        return history.push(backUrl);
    };

    const { carrier, shipVia, requestedDeliveryDateDisplay, requestedPickupDateDisplay, requiresApproval } = cart;

    const canModifyCart = !(cart && cart.type === "Quote");

    const goToStep = (step: number) => {
        dispatch({ type: "xCheckoutStepper/update", step });
    };

    const paymentStepNumberInCheckout = () => {
        return isPickUp ? 1 : 2;
    };

    const paymetnStepNumber = paymentStepNumberInCheckout();

    const carriersWithAdditionalFieldsIds = cart?.properties ? cart?.properties?.carriersWithAdditionalFieldsIds : "";

    const carriersWithAdditionalFieldsIdsList =
        (carriersWithAdditionalFieldsIds && carriersWithAdditionalFieldsIds.split(",")) || "";

    return (
        <StyledWrapper
            css={css`
                ${checkoutMainElementStyles.sectionContainer?.css}
                margin-top: -38px;
            `}
        >
            <StyledWrapper {...checkoutMainElementStyles.sectionBodyContainer} className="flex-column">
                {/* Addresses Step review */}
                <StyledWrapper {...styles.stepWrapper}>
                    <StyledWrapper {...styles.stepReviewTitle}>
                        <Typography {...styles.stepReviewTitleText}>{translate("Addresses")}</Typography>
                        <StyledWrapper {...styles.stepReviewTitleOthers}>
                            <Clickable
                                aria-label={translate("Edit Address")}
                                {...styles.clickableLinkStyles}
                                onClick={goBackToShipping}
                            >
                                <Icon src="Create" {...styles.editIconStyles}></Icon>
                                {translate("Edit")}
                            </Clickable>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.stepReviewContent}>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.gridItemStyles}>
                                <StyledWrapper
                                    className={isGuest ? "item-content-wrapper guest" : "item-content-wrapper"}
                                >
                                    <BillingAddressInfoDisplay billTo={billToState.value} />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.gridItemStyles}>
                                <StyledWrapper
                                    className={isGuest ? "item-content-wrapper guest" : "item-content-wrapper"}
                                >
                                    {fulfillmentMethod === FulfillmentMethod.Ship && (
                                        <ShippingAddressInfoDisplay
                                            shipTo={shipToState.value}
                                            cartPageLink={cartPageUrl}
                                            isQuote={cart.type === "Quote"}
                                        />
                                    )}
                                    {fulfillmentMethod === FulfillmentMethod.PickUp && (
                                        <>
                                            {cart?.type === "Quote" &&
                                                cart?.properties.originalPickupFullAddress !== undefined &&
                                                cart?.properties.originalPickupFullAddress !== "" && (
                                                    <StyledWrapper {...styles.pickupheading}>
                                                        <Typography {...styles.headingText} as="h3">
                                                            {translate("Pick Up Address")}
                                                        </Typography>
                                                        <StyledWrapper
                                                            css={css`
                                                                flex-direction: column;
                                                                width: 170px;
                                                                font-size: 16px;
                                                            `}
                                                        >
                                                            {cart?.properties.originalPickupFullAddress}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                )}
                                            {(cart?.properties.originalPickupFullAddress === undefined ||
                                                cart?.properties.originalPickupFullAddress === "") && (
                                                <PickUpLocationAddressInfoDisplay
                                                    location={pickUpWarehouse!}
                                                    cartPageLink={cartPageUrl}
                                                    isQuote={cart.type === "Quote"}
                                                />
                                            )}
                                        </>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
                {/* Shipping Step review */}
                {!isPickUp && (
                    <StyledWrapper {...styles.stepWrapper}>
                        <StyledWrapper {...styles.stepReviewTitle}>
                            <Typography {...styles.stepReviewTitleText}>{translate("Shipping")}</Typography>
                            <StyledWrapper {...styles.stepReviewTitleOthers}>
                                <Clickable
                                    {...styles.clickableLinkStyles}
                                    onClick={() => {
                                        goToStep(1);
                                    }}
                                >
                                    <Icon src="Create" {...styles.editIconStyles}></Icon>
                                    {translate("Edit")}
                                </Clickable>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.stepReviewContent}>
                            <GridContainer {...styles.container}>
                                <GridItem {...styles.gridItemStyles}>
                                    <StyledWrapper className="item-content-wrapper">
                                        <Typography {...styles.labelTypo}>{translate("Shipping Carrier")}</Typography>
                                        {carrier && carrier.description && (
                                            <Typography {...styles.valueTypo}>{carrier.description}</Typography>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.gridItemStyles}>
                                    <StyledWrapper className="item-content-wrapper">
                                        <Typography {...styles.labelTypo}>{translate("Shipping Service")}</Typography>
                                        {shipVia && shipVia.description && (
                                            <Typography {...styles.valueTypo}>{shipVia.description}</Typography>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                {xShipAccountNumber && (
                                    <GridItem {...styles.gridItemStyles}>
                                        <StyledWrapper className="item-content-wrapper">
                                            <Typography {...styles.labelTypo}>{translate("Account Number")}</Typography>
                                            <Typography {...styles.valueTypo}>{xShipAccountNumber}</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                {xShipNameOfCarrier && (
                                    <GridItem {...styles.gridItemStyles}>
                                        <StyledWrapper className="item-content-wrapper">
                                            <Typography {...styles.labelTypo}>
                                                {translate("Name Of Carrier")}
                                            </Typography>
                                            <Typography {...styles.valueTypo}>{xShipNameOfCarrier}</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                {carrier &&
                                    carrier?.id &&
                                    carriersWithAdditionalFieldsIdsList &&
                                    carriersWithAdditionalFieldsIdsList?.length &&
                                    carriersWithAdditionalFieldsIdsList.includes(carrier?.id) && (
                                        <GridItem {...styles.gridItemStyles}>
                                            <StyledWrapper className="item-content-wrapper">
                                                <Typography {...styles.labelTypo}>
                                                    {translate("Shipping Notes")}
                                                </Typography>
                                                <Typography {...styles.valueTypo}>
                                                    {xShipShippingNotes.length > 0
                                                        ? xShipShippingNotes
                                                        : translate("- -")}
                                                </Typography>
                                            </StyledWrapper>
                                        </GridItem>
                                    )}
                            </GridContainer>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {/* Payment Step review */}
                {!requiresApproval && (
                    <StyledWrapper {...styles.stepWrapper}>
                        <StyledWrapper {...styles.stepReviewTitle}>
                            <Typography {...styles.stepReviewTitleText}>{translate("Payment")}</Typography>
                            <StyledWrapper {...styles.stepReviewTitleOthers}>
                                <Clickable
                                    {...styles.clickableLinkStyles}
                                    onClick={() => {
                                        goToStep(paymetnStepNumber);
                                    }}
                                >
                                    <Icon src="Create" {...styles.editIconStyles}></Icon>
                                    {translate("Edit")}
                                </Clickable>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.stepReviewContent}>
                            <GridContainer {...styles.container}>
                                {xPaymentMethod && xPaymentMethod.toLowerCase() !== "terms" && (
                                    <GridItem {...styles.gridItemStyles}>
                                        <StyledWrapper className="item-content-wrapper">
                                            <Typography {...styles.labelTypo}>{translate("Payment Method")}</Typography>
                                            {xPaymentMethod.toLowerCase() === "cash" && (
                                                <Typography {...styles.valueTypo}>
                                                    {xCcType.charAt(0).toUpperCase() + xCcType.slice(1)}{" "}
                                                    {translate("****")} {xLastFour} &nbsp;&nbsp;&nbsp;
                                                    {translate("Exp")}&nbsp;
                                                    {xExpDateMon}/{xExpDateYear}
                                                </Typography>
                                            )}
                                            {xPaymentMethod.toLowerCase() !== "cash" && (
                                                <Typography {...styles.valueTypo}>{xPaymentProfile}</Typography>
                                            )}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                {xPaymentMethod && xPaymentMethod.toLowerCase() === "terms" && (
                                    <GridItem {...styles.gridItemStyles}>
                                        <StyledWrapper className="item-content-wrapper">
                                            <Typography {...styles.labelTypo}>{translate("Payment Method")}</Typography>
                                            <Typography {...styles.valueTypo}>{xPaymentMethod}</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                {xPo && (
                                    <GridItem {...styles.gridItemStyles}>
                                        <StyledWrapper className="item-content-wrapper">
                                            <Typography {...styles.labelTypo}>{translate("Purchase Order")}</Typography>
                                            <Typography {...styles.valueTypo}>{xPo}</Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.stepWrapper}>
                    <StyledWrapper {...styles.stepReviewTitle}>
                        <Typography {...styles.stepReviewTitleText}>{translate("Products")}</Typography>
                        {canModifyCart && (
                            <StyledWrapper {...styles.stepReviewTitleOthers}>
                                <Clickable
                                    {...styles.clickableLinkStyles}
                                    href={cartPageUrl}
                                    className="continue-shopping"
                                >
                                    <Icon src="Create" {...styles.editIconStyles}></Icon>
                                    {translate("Edit")}
                                </Clickable>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.stepReviewContent} className="cartlines">
                        <Zone zoneName="Content1" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitShippingInfo)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Review & Confirm",
    },
};

export default widgetModule;
